import { z } from 'zod';

import { Id, IsoDateTime } from '../common/primitives';

// ── Enums ────────────────────────────────────────────────────────────────────

export const ImportJobModeSchema = z.enum(['DRY_RUN', 'LIVE']);
export type ImportJobMode = z.infer<typeof ImportJobModeSchema>;

export const ImportJobStatusSchema = z.enum([
  'QUEUED',
  'VALIDATING',
  'RUNNING',
  'PAUSING',
  'PAUSED',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
  'UNDOING',
  'UNDONE',
]);
export type ImportJobStatus = z.infer<typeof ImportJobStatusSchema>;

export const ImportRowStatusSchema = z.enum([
  'PENDING',
  'RUNNING',
  'CREATED',
  'UPDATED',
  'SKIPPED',
  'FAILED',
]);
export type ImportRowStatus = z.infer<typeof ImportRowStatusSchema>;

// ── Run options ──────────────────────────────────────────────────────────────

/**
 * One explicit Brand → Vendor rule.
 *
 * `vendorStoreName` is matched against existing vendors by normalised name
 * first; only when `createIfMissing` is set will the importer provision a new
 * vendor (plus its login user and default warehouse). This is the *only*
 * sanctioned way to derive a vendor from a brand — the pipeline never infers
 * one otherwise.
 */
export const VendorMappingRuleSchema = z.object({
  brand: z.string().trim().min(1),
  vendorStoreName: z.string().trim().min(1),
  createIfMissing: z.boolean().default(false),
});
export type VendorMappingRule = z.infer<typeof VendorMappingRuleSchema>;

/**
 * An explicit alias for a vendor name observed in the source.
 *
 * Source sites routinely print a legal or trading name that differs from the
 * store name on this platform ("Yogiraj Food Products" vs "Yogiraj Foods").
 * An alias states that equivalence outright — the resolver never infers one by
 * fuzzy-matching, because two genuinely different sellers can have very similar
 * names and the cost of confusing them is paying the wrong party.
 */
export const VendorAliasSchema = z.object({
  /** The name as it appears in the sheet or on the product page. */
  sourceName: z.string().trim().min(1),
  /** The existing store it refers to. */
  vendorStoreName: z.string().trim().min(1),
});
export type VendorAlias = z.infer<typeof VendorAliasSchema>;

export const ImportOptionsSchema = z.object({
  /**
   * Explicit Brand → Vendor mapping. Takes precedence over every other vendor
   * signal (see `vendorSourcePreference`). Empty means "no mapping supplied".
   */
  vendorMappings: z.array(VendorMappingRuleSchema).default([]),
  /**
   * Name equivalences applied to the sheet's and the product page's vendor
   * names before they are looked up. Does not outrank `vendorMappings`.
   */
  vendorAliases: z.array(VendorAliasSchema).default([]),
  /**
   * Which side wins when the sheet and the live product page disagree about the
   * vendor. An explicit `vendorMappings` hit always wins over both.
   */
  vendorSourcePreference: z.enum(['PRODUCT_PAGE', 'SHEET']).default('PRODUCT_PAGE'),
  /** Fetch missing/corrupt fields from the row's Product URL. */
  enrichFromProductUrl: z.boolean().default(true),
  /** Crawl the source site's category tree to recover per-product categories. */
  enrichCategoriesFromSite: z.boolean().default(true),
  /**
   * Where to file a product whose category cannot be recovered from *any*
   * source — some catalogs contain products the storefront never lists under a
   * category. Written as a path, e.g. `"Uncategorised"` or `"Food > Other"`.
   *
   * Left unset, such a row fails rather than being filed somewhere invented.
   * This is an operator decision, never a default.
   */
  fallbackCategoryPath: z.string().trim().optional(),
  /** Download + upload main/gallery images. Off leaves existing media untouched. */
  importImages: z.boolean().default(true),
  /** Max gallery images stored per product (the main image counts as one). */
  maxImagesPerProduct: z.number().int().min(1).max(8).default(4),
  /**
   * Directory of pre-downloaded images, resolved relative to the repo root.
   * Checked before any network fetch; a hit avoids a download entirely.
   */
  localImageDir: z.string().trim().optional(),
  /**
   * HSN/SAC applied to rows whose source carries none. `Product.hsnSac` is
   * NOT NULL, so without this a source lacking HSN cannot be imported — the
   * pre-import validation reports those rows rather than inventing a code.
   */
  defaultHsnSac: z.string().trim().optional(),
  /** GST rate (%) for rows whose source carries none. */
  defaultGstRate: z.number().int().min(0).max(28).default(18),
  /** Status new products land in. Existing products keep their current status. */
  productStatus: z.enum(['DRAFT', 'PENDING_APPROVAL', 'APPROVED']).default('APPROVED'),
  /** Politeness delay between outbound requests to the source site, in ms. */
  requestDelayMs: z.number().int().min(0).max(10_000).default(250),
  /** Abort a single outbound request after this long. */
  requestTimeoutMs: z.number().int().min(1_000).max(120_000).default(20_000),
  /** Stop the whole run once this many rows have failed. 0 disables the circuit breaker. */
  maxConsecutiveFailures: z.number().int().min(0).max(1_000).default(0),
});
export type ImportOptions = z.infer<typeof ImportOptionsSchema>;

// ── Validation payloads ──────────────────────────────────────────────────────

export const ImportIssueSchema = z.object({
  code: z.string(),
  message: z.string(),
  /** 1-based source row, or null for run-wide issues. */
  row: z.number().int().nullable().default(null),
  severity: z.enum(['ERROR', 'WARNING']),
});
export type ImportIssue = z.infer<typeof ImportIssueSchema>;

export const PreValidationSchema = z.object({
  checkedAt: IsoDateTime,
  totalRows: z.number().int(),
  importableRows: z.number().int(),
  blockingRows: z.number().int(),
  issues: z.array(ImportIssueSchema),
});
export type PreValidation = z.infer<typeof PreValidationSchema>;

export const PostValidationSchema = z.object({
  checkedAt: IsoDateTime,
  passed: z.boolean(),
  checks: z.array(
    z.object({
      name: z.string(),
      passed: z.boolean(),
      detail: z.string(),
    }),
  ),
});
export type PostValidation = z.infer<typeof PostValidationSchema>;

// ── Job + row shapes ─────────────────────────────────────────────────────────

/** What an undo actually managed to put back. */
export const UndoReportSchema = z.object({
  undoneAt: IsoDateTime,
  deleted: z.number().int(),
  restored: z.number().int(),
  skipped: z.number().int(),
  failed: z.number().int(),
  problems: z.array(z.string()),
  /** Relations left in place because other products may depend on them. */
  notRolledBack: z.array(z.string()),
  note: z.string(),
});
export type UndoReport = z.infer<typeof UndoReportSchema>;

export const ImportJobSchema = z.object({
  id: Id,
  mode: ImportJobModeSchema,
  status: ImportJobStatusSchema,
  sourceName: z.string(),
  sourceUrl: z.string(),
  options: ImportOptionsSchema,
  totalRows: z.number().int(),
  processedRows: z.number().int(),
  createdCount: z.number().int(),
  updatedCount: z.number().int(),
  skippedCount: z.number().int(),
  failedCount: z.number().int(),
  vendorsCreated: z.number().int(),
  categoriesCreated: z.number().int(),
  brandsCreated: z.number().int(),
  imagesDownloaded: z.number().int(),
  imagesUploaded: z.number().int(),
  preValidation: PreValidationSchema.nullable(),
  postValidation: PostValidationSchema.nullable(),
  error: z.string().nullable(),
  undoneAt: IsoDateTime.nullable(),
  undoReport: UndoReportSchema.nullable(),
  actorId: z.string().nullable(),
  startedAt: IsoDateTime.nullable(),
  finishedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type ImportJob = z.infer<typeof ImportJobSchema>;

export const ImportRowSchema = z.object({
  id: Id,
  jobId: Id,
  rowNumber: z.number().int(),
  status: ImportRowStatusSchema,
  sourceKey: z.string().nullable(),
  raw: z.record(z.unknown()),
  resolved: z.record(z.unknown()).nullable(),
  messages: z.array(z.string()),
  error: z.string().nullable(),
  attempts: z.number().int(),
  productId: z.string().nullable(),
  updatedAt: IsoDateTime,
});
export type ImportRow = z.infer<typeof ImportRowSchema>;

// ── Requests ─────────────────────────────────────────────────────────────────

export const CreateImportJobRequestSchema = z.object({
  sourceName: z.string().trim().min(1),
  sourceUrl: z.string().trim().min(1),
  mode: ImportJobModeSchema.default('DRY_RUN'),
  options: ImportOptionsSchema.partial().default({}),
});
export type CreateImportJobRequest = z.infer<typeof CreateImportJobRequestSchema>;

export const ListImportJobsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  status: ImportJobStatusSchema.optional(),
});
export type ListImportJobsQuery = z.infer<typeof ListImportJobsQuerySchema>;

export const ListImportRowsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  status: ImportRowStatusSchema.optional(),
});
export type ListImportRowsQuery = z.infer<typeof ListImportRowsQuerySchema>;

/** Default vendor mapping shipped with the module, surfaced to the admin UI. */
export const ImportDefaultsSchema = z.object({
  vendorMappings: z.array(VendorMappingRuleSchema),
  options: ImportOptionsSchema,
});
export type ImportDefaults = z.infer<typeof ImportDefaultsSchema>;
